// src/authService.js
import { signInWithCustomToken, signOut } from "firebase/auth";
import { auth } from "./firebase";

/**
 * Flujo completo de autenticación:
 * 1. Login: el endpoint /login devuelve un customToken de Firebase
 * 2. Intercambio en el cliente: usa el customToken con el SDK de Firebase para obtener un ID token
 * 3. Request: envía el ID token en el header Authorization
 */

/**
 * Paso 2: Intercambia el customToken por ID token usando Firebase SDK
 * Después de recibir el customToken del endpoint /login, usa signInWithCustomToken
 * para autenticar con Firebase y obtener el ID token real.
 * 
 * @param {string} customToken - El custom token recibido del endpoint /login
 * @returns {Promise<string>} - El ID token de Firebase para usar en requests autenticadas
 */
export async function signInWithFirebaseToken(customToken) {
  try {
    console.log("🔍 [AuthService] Iniciando intercambio de customToken...");
    console.log("🔍 [AuthService] Longitud del token:", customToken?.length || 0);
    
    // Validar que el token existe y no está vacío
    if (!customToken || typeof customToken !== "string" || customToken.trim().length === 0) {
      throw new Error("El customToken está vacío o no es válido");
    }
    
    // Validar que el token tenga una longitud mínima razonable (un JWT típico tiene al menos 200 caracteres)
    if (customToken.trim().length < 100) {
      console.error("❌ [AuthService] El token es demasiado corto para ser un JWT válido");
      console.error("❌ [AuthService] Longitud:", customToken.trim().length);
      console.error("❌ [AuthService] Token completo:", customToken);
      throw new Error("El token recibido es demasiado corto. Un custom token de Firebase debe tener al menos 200 caracteres. El backend podría estar devolviendo un token incorrecto.");
    }
    
    // Validar formato básico del token (debe tener exactamente 3 partes separadas por puntos)
    const tokenParts = customToken.trim().split(".");
    if (tokenParts.length !== 3) {
      console.error("❌ [AuthService] El token no tiene el formato correcto de JWT");
      console.error("❌ [AuthService] Número de partes:", tokenParts.length);
      console.error("❌ [AuthService] Token recibido (primeros 100 caracteres):", customToken.substring(0, 100));
      throw new Error("El customToken no tiene el formato correcto de Firebase custom token. Debe ser un JWT con 3 partes separadas por puntos.");
    }

    // Validar que cada parte del token tenga contenido
    if (tokenParts[0].length === 0 || tokenParts[1].length === 0 || tokenParts[2].length === 0) {
      console.error("❌ [AuthService] Una o más partes del token están vacías");
      throw new Error("El token está mal formado: una o más partes están vacías.");
    }

    // Intentar decodificar el payload del JWT para verificar que tenga el uid
    let payload;
    try {
      // Decodificar el payload (segunda parte del JWT)
      const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      // Agregar padding si es necesario
      const paddedPayload = base64Payload + '='.repeat((4 - base64Payload.length % 4) % 4);
      const decodedPayload = atob(paddedPayload);
      payload = JSON.parse(decodedPayload);
      
      console.log("🔍 [AuthService] Payload del token decodificado:", {
        uid: payload.uid,
        iss: payload.iss,
        aud: payload.aud,
        exp: payload.exp,
        iat: payload.iat,
        hasUid: !!payload.uid,
        keys: Object.keys(payload)
      });
      
      // Verificar que el token tenga un uid (requerido para custom tokens de Firebase)
      if (!payload.uid || typeof payload.uid !== "string" || payload.uid.trim().length === 0) {
        console.error("❌ [AuthService] El token no contiene un 'uid' válido en el payload");
        console.error("❌ [AuthService] Payload completo:", JSON.stringify(payload, null, 2));
        console.error("❌ [AuthService] El backend debe generar el token usando: admin.auth().createCustomToken(uid)");
        throw new Error(
          "El custom token no contiene un identificador de usuario (uid) válido. " +
          "El backend debe generar el token con Firebase Admin SDK usando: admin.auth().createCustomToken(uid). " +
          "Verifica que el backend esté configurado correctamente."
        );
      }

      // Verificar que el issuer sea correcto (debe ser el projectId de Firebase)
      const expectedIss = `https://securetoken.google.com/${auth.app.options.projectId}`;
      if (payload.iss !== expectedIss) {
        console.warn("⚠️ [AuthService] El issuer del token no coincide con el esperado");
        console.warn("⚠️ [AuthService] Issuer recibido:", payload.iss);
        console.warn("⚠️ [AuthService] Issuer esperado:", expectedIss);
        console.warn("⚠️ [AuthService] Esto podría indicar que el token no fue generado para este proyecto de Firebase");
      }
    } catch (decodeError) {
      if (decodeError.message && decodeError.message.includes("uid")) {
        // Re-lanzar el error de uid si ya lo detectamos
        throw decodeError;
      }
      console.error("❌ [AuthService] No se pudo decodificar el payload del token:", decodeError);
      console.error("❌ [AuthService] Esto podría indicar que el token no es un JWT válido");
      console.error("❌ [AuthService] Token (primeros 200 caracteres):", customToken.substring(0, 200));
      throw new Error(
        "No se pudo decodificar el token. El token podría no ser un JWT válido o estar corrupto. " +
        "El backend debe devolver un custom token de Firebase generado con admin.auth().createCustomToken(uid)."
      );
    }
    
    console.log("🔍 [AuthService] Token validado correctamente. Intentando autenticar con Firebase...");
    
    // Paso importante: usar el customToken devuelto por el backend
    // NOTA: Si el backend devuelve un JWT normal (no un custom token de Firebase),
    // este paso fallará. El backend debe usar Firebase Admin SDK para generar
    // el custom token: admin.auth().createCustomToken(uid)
    const userCredential = await signInWithCustomToken(auth, customToken);
    
    console.log("✅ [AuthService] Autenticación con Firebase exitosa");
    console.log("🔍 [AuthService] Usuario autenticado:", userCredential.user.uid);
    
    // Luego obtener el ID token real de Firebase
    const idToken = await userCredential.user.getIdToken();
    
    if (!idToken) {
      throw new Error("No se pudo obtener el ID token de Firebase después de la autenticación");
    }
    
    console.log("✅ [AuthService] ID token obtenido exitosamente");
    
    // Guardar el ID token en localStorage
    localStorage.setItem("token", idToken);
    localStorage.setItem("authToken", idToken);
    localStorage.setItem("iOS_token", idToken);
    
    return idToken;
  } catch (error) {
    console.error("❌ [AuthService] Error al intercambiar customToken:", error);
    console.error("❌ [AuthService] Error code:", error.code);
    console.error("❌ [AuthService] Error message:", error.message);
    console.error("❌ [AuthService] Stack:", error.stack);
    
    // Log adicional del token para debugging (sin exponer el token completo)
    if (customToken && typeof customToken === "string") {
      const tokenPreview = customToken.length > 50 
        ? `${customToken.substring(0, 50)}... (${customToken.length} caracteres)`
        : customToken;
      console.error("❌ [AuthService] Token que causó el error:", tokenPreview);
    }
    
    // Mensaje más descriptivo para el usuario
    if (error.code === "auth/missing-identifier" || error.code === "auth/invalid-custom-token") {
      console.error("❌ [AuthService] ERROR ESPECÍFICO:", error.code);
      console.error("❌ [AuthService] Este error generalmente ocurre cuando:");
      console.error("   1. El custom token no fue generado con Firebase Admin SDK");
      console.error("   2. El token no contiene un 'uid' válido en el payload");
      console.error("   3. El token está mal formado o corrupto");
      console.error("   4. El token fue generado para un proyecto de Firebase diferente");
      console.error("❌ [AuthService] El backend debe usar: admin.auth().createCustomToken(uid)");
      console.error("❌ [AuthService] Project ID esperado:", auth.app.options.projectId);
      
      // Intentar decodificar el token nuevamente para mostrar información de diagnóstico
      try {
        const tokenParts = customToken.split(".");
        if (tokenParts.length === 3) {
          const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
          const paddedPayload = base64Payload + '='.repeat((4 - base64Payload.length % 4) % 4);
          const decodedPayload = atob(paddedPayload);
          const payload = JSON.parse(decodedPayload);
          console.error("❌ [AuthService] Información del token recibido:");
          console.error("   - UID en payload:", payload.uid || "NO ENCONTRADO");
          console.error("   - Issuer:", payload.iss || "NO ENCONTRADO");
          console.error("   - Keys en payload:", Object.keys(payload));
        }
      } catch (diagError) {
        console.error("❌ [AuthService] No se pudo decodificar el token para diagnóstico");
      }
      
      const detailedError = new Error(
        "El backend no está devolviendo un custom token de Firebase válido. " +
        "El backend debe usar Firebase Admin SDK para generar el token: " +
        "admin.auth().createCustomToken(uid). " +
        "Verifica que el backend esté configurado correctamente y que esté usando el mismo proyecto de Firebase."
      );
      detailedError.originalError = error;
      detailedError.code = error.code;
      throw detailedError;
    }
    
    throw error;
  }
}

/**
 * Paso 3: Obtiene el ID token actual para usar en requests
 * Firebase maneja automáticamente la renovación de tokens, así que normalmente
 * no necesitamos forzar el refresh (a menos que sepamos que el token expiró)
 * 
 * @param {boolean} forceRefresh - Si es true, fuerza la renovación del token
 * @returns {Promise<string|null>} - El ID token de Firebase o null si no hay usuario autenticado
 */
export async function getFirebaseToken(forceRefresh = false) {
  try {
    // Esperar un momento si el usuario aún no está disponible (puede estar restaurando)
    let currentUser = auth.currentUser;
    if (!currentUser) {
      // Dar tiempo a Firebase para restaurar la sesión desde el almacenamiento persistente
      // Esperar hasta 1 segundo en incrementos de 100ms
      for (let i = 0; i < 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        currentUser = auth.currentUser;
        if (currentUser) break;
      }
    }
    
    if (!currentUser) {
      return null;
    }
    
    // Obtener el ID token de Firebase
    // Si forceRefresh es false, Firebase usará el token cacheado si aún es válido
    // Si es true, forzará la renovación (útil cuando sabemos que el token expiró)
    const idToken = await currentUser.getIdToken(forceRefresh);
    
    if (!idToken) {
      console.error("❌ [AuthService] No se pudo obtener el token");
      return null;
    }
    
    // Actualizar localStorage con el token (fresco o cacheado)
    localStorage.setItem("token", idToken);
    localStorage.setItem("authToken", idToken);
    localStorage.setItem("iOS_token", idToken);
    
    return idToken;
  } catch (error) {
    console.error("❌ [AuthService] Error al obtener token:", error);
    // Si el error es de autenticación, limpiar el estado solo si es un error definitivo
    if (error.code === 'auth/user-token-expired' || 
        error.code === 'auth/user-disabled' ||
        error.code === 'auth/user-not-found') {
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("iOS_token");
    }
    return null;
  }
}

export async function logout() {
  // Marcar que se está haciendo logout intencionalmente
  // Esto previene que el Login redirija automáticamente
  localStorage.setItem("logout_intentional", "true");
  
  await signOut(auth);
  
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  localStorage.removeItem("iOS_token");
  localStorage.removeItem("userId");
}


